import {ImageIcon} from '@/components/svg/ImageIcon';
import {SendIcon} from '@/components/svg/SendIcon';
import {AttachmentIcon} from '@chakra-ui/icons';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import {DOCUMENTS_ACCEPT, IMAGES_ACCEPT} from 'ff-common';
import {RefObject} from 'react';
import {useTranslation} from 'react-i18next';
import {ConversationFichiersPreview} from './ConversationFichiersPreview';

/**
 * Zone de saisie de message avec boutons pièces jointes et envoi.
 * Inclut la preview des fichiers sélectionnés.
 */
export const ConversationMessageInputArea: React.FC<{
  messageContent: string;
  setMessageContent: (content: string) => void;
  selectedFiles: File[];
  isSubmitting: boolean;
  peutEnvoyer: boolean;
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveFile: (index: number) => void;
  envoyerMessage: () => void;
  documentInputRef: RefObject<HTMLInputElement | null>;
  imageInputRef: RefObject<HTMLInputElement | null>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  placeholder?: string;
  /** Si true, masque les boutons pièce jointe (pour conversations terminées) */
  hidePieceJointeButtons?: boolean;
  /** Hauteur minimale du textarea (responsive) */
  textareaMinH?: string | {base?: string; md?: string; lg?: string};
}> = ({
  messageContent,
  setMessageContent,
  selectedFiles,
  isSubmitting,
  peutEnvoyer,
  handleFileSelect,
  handleRemoveFile,
  envoyerMessage,
  documentInputRef,
  imageInputRef,
  textareaRef,
  placeholder,
  hidePieceJointeButtons = false,
  textareaMinH = '40px',
}) => {
  const {t} = useTranslation();

  // Envoyer avec Entrée
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      envoyerMessage();
    }
  };

  return (
    <Box
      p={4}
      pb={{base: 4, md: 10}}
      bg='gray.bg'
      borderRight='1px solid'
      borderColor='gray.light'
    >
      {/* Preview des fichiers sélectionnés (avant envoi) */}
      <ConversationFichiersPreview
        selectedFiles={selectedFiles}
        onRemoveFile={handleRemoveFile}
      />

      <Box
        bg='white'
        borderRadius='8px'
      >
        {/* Sélecteur de fichier documents (déclenché par le bouton trombone) */}
        <input
          type='file'
          ref={documentInputRef}
          style={{display: 'none'}}
          onChange={handleFileSelect}
          accept={DOCUMENTS_ACCEPT}
          multiple
        />
        {/* Sélecteur de fichier images (déclenché par le bouton image) */}
        <input
          type='file'
          ref={imageInputRef}
          style={{display: 'none'}}
          onChange={handleFileSelect}
          accept={IMAGES_ACCEPT}
          multiple
        />
        {/* Zone de saisie du message */}
        <Textarea
          placeholder={
            placeholder ?? t('back_office.conversations.placeholder_message')
          }
          value={messageContent}
          onChange={(e) => {
            setMessageContent(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          isDisabled={isSubmitting}
          fontSize='sm'
          bg='white'
          border='none'
          resize='none'
          minH={textareaMinH}
          maxH='150px'
          height='auto'
          rows={1}
          overflow='auto'
          _focus={{border: 'none', boxShadow: 'none'}}
          ref={textareaRef}
          px={3}
          pt={2}
          pb={0}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            target.style.height = `${target.scrollHeight}px`;
          }}
        />
        {/* Icônes pièce jointe et bouton envoyer */}
        <HStack
          spacing={1}
          pl={3}
          pr={2}
          pb={2}
          justify='space-between'
        >
          {/* Icônes pièce jointe (masquées si hidePieceJointeButtons) */}
          {!hidePieceJointeButtons ? (
            <HStack spacing={1}>
              <Tooltip
                label={t('back_office.conversations.ajouter_piece_jointe')}
              >
                <IconButton
                  aria-label={t(
                    'back_office.conversations.ajouter_piece_jointe',
                  )}
                  icon={<AttachmentIcon />}
                  variant='ghost'
                  color='gray.dark'
                  size='sm'
                  onClick={() => {
                    documentInputRef.current?.click();
                  }}
                  isDisabled={isSubmitting}
                />
              </Tooltip>
              <Tooltip label={t('back_office.conversations.ajouter_image')}>
                <IconButton
                  aria-label={t('back_office.conversations.ajouter_image')}
                  icon={<ImageIcon />}
                  variant='ghost'
                  color='gray.dark'
                  size='sm'
                  onClick={() => {
                    imageInputRef.current?.click();
                  }}
                  isDisabled={isSubmitting}
                />
              </Tooltip>
            </HStack>
          ) : (
            <Box /> // Espace vide pour garder le bouton à droite
          )}
          {/* Bouton envoyer */}
          <Button
            colorScheme='blue'
            bg='blue.main'
            color='white'
            onClick={envoyerMessage}
            isLoading={isSubmitting}
            isDisabled={!peutEnvoyer}
            _hover={{bg: 'blue.dark'}}
            leftIcon={<SendIcon />}
          >
            {t('back_office.conversations.envoyer')}
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};
